/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useMemo } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import type { ConfusionMatrixData } from '../../../types';

interface Props {
  data: ConfusionMatrixData;
  title?: string;
}

export default function ConfusionMatrix({ data, title = 'Confusion Matrix' }: Props) {
  const { labels, matrix } = data;

  const maxVal = useMemo(() => {
    let max = 0;
    for (const row of matrix) {
      for (const v of row) {
        if (v > max) max = v;
      }
    }
    return max || 1;
  }, [matrix]);

  const getColor = (val: number) => {
    const intensity = Math.round((val / maxVal) * 200);
    return `rgba(25, 118, 210, ${intensity / 255})`;
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        {title}
      </Typography>
      <Box sx={{ overflowX: 'auto' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `80px repeat(${labels.length}, 1fr)`,
            gridTemplateRows: `32px repeat(${labels.length}, 1fr)`,
            gap: '2px',
            minWidth: 200,
          }}
        >
          {/* Top-left corner */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Act\Pred
            </Typography>
          </Box>
          {/* Column headers */}
          {labels.map((label) => (
            <Box
              key={`col-${label}`}
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <Typography variant="caption" fontWeight={600}>
                {label}
              </Typography>
            </Box>
          ))}
          {/* Rows */}
          {matrix.map((row, i) => (
            <React.Fragment key={`row-${i}`}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="caption" fontWeight={600}>
                  {labels[i]}
                </Typography>
              </Box>
              {row.map((val, j) => (
                <Box
                  key={`${i}-${j}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: getColor(val),
                    borderRadius: 0.5,
                    minHeight: 40,
                    border: i === j ? '2px solid #1565c0' : '1px solid #e0e0e0',
                  }}
                >
                  <Typography variant="body2" fontWeight={i === j ? 700 : 400}>
                    {val}
                  </Typography>
                </Box>
              ))}
            </React.Fragment>
          ))}
        </Box>
      </Box>
    </Paper>
  );
}
